import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	useMemo,
	useRef,
	ReactNode
} from 'react'
import { useBeforeUnload } from 'react-router-dom'
import toast from 'react-hot-toast'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import { useRecentClientPaymentConfirmation } from 'components/dashboard/newTransaction/hooks/useRecentClientPaymentConfirmation'
import {
	checkIsCardDeclinedError,
	checkIsPaymentCancelledError,
	checkNoActivePaymentMethodError
} from '../util/stripe'
import { useCurrentPractice } from './PracticeContext'
import {
	cancelReaderPayment,
	getTerminalConnectionToken,
	processReaderPayment,
	simulateReaderSwipe,
	deleteSimulatedReader
} from '../apis/checkoutPaymentService'
import { capturePaymentIntent, createPaymentIntent } from '../apis/paymentIntent'
import { convertDollarsToCents } from '@scratchpay/checkout-core'
import { displayLoadingToast, warningToast, isProductionEnv } from '../util/util'
import errorToast from '../util/errorToast'
import { isMobileDevice } from '../util/mobile'
import {
	DENIED_AMEX_CARD_MESSAGE,
	PAYMENT_CANCELED,
	STATE_AMEX_BLOCKED,
	STATE_FAILED,
	STATE_SUCCEEDED,
	PAYMENT_STATUS_TIMEOUT,
	PAYMENT_STATUS_UNCONFIRMED_MESSAGE
} from '../constants'
import {
	isMVPPractice,
	isSurchargeEnabled,
	blockAmexPayments as blockAmexPractice,
	isServerDrivenTerminalEnabled,
	isLendingAtCheckoutEnabled,
	shouldHideReceiptModal as practiceShouldHideReceiptModal,
	isDonationEnabled,
	isTerminalTippingEnabled,
	getTerminalTippingPromptByDefault
} from '../util/practice'
import {
	blockAmexPayments as blockAmexConsolidator,
	shouldRequireInvoiceNumber,
	shouldHideReceiptModal as consolidatorShouldHideReceiptModal,
	isDonationEnabled as consolidatorIsDonationEnabled
} from '../util/consolidator'
import { useClientIdValidator } from '../components/dashboard/client/hooks'
import { createSimulatedReader } from '../apis/reader'
import { selectUser } from '../selectors/auth'
import useInvoiceNumberValidator from '../components/dashboard/newTransaction/hooks/use-invoice-number-validator'
import {
	usePaymentIntentStatusWatcher,
	type PaymentIntentStatusAwaiter
} from '../hooks/usePaymentIntentStatusWatcher'
import type { SelectedTipRecipient } from '../components/dashboard/newTransaction/hooks/useTipRecipients'
import { logError } from '../observability/logger'
import { resolveReaderSendError, READER_SEND_ERROR_OUTCOME } from '../services/reader'

export interface IReaderContext {
	serverDrivenTerminal?
	terminal? // IStripeTerminal;
	cancelPayment?
	chargeAmount?
	clearReaderDisplay?
	collectCardPayment?
	connectionStatus?
	connectToReader?
	connectReaderHandler?

	setIsInteracTransaction?
	isInteracTransaction?

	disconnectReader?
	discoveredReaders?
	setDiscoveredReaders?
	discoverReaderHandler?
	discoveryInProgress?

	handleChargeAmountChange?
	handleClientID?
	handleDescriptionChange?
	handleInvoiceIDChange?

	setUseSimulatedReader?
	useSimulatedReader?
	simulateSwipe?

	processingTerminalPayment?

	reader?
	readerSelectionInProgress?
	unsavedChanges?
	setUnsavedChanges?
	connectToServerDrivenReader?
	cancelServerDrivenReaderPayment?
	chargeAmountError?
	clientIdError?
	invoiceIdError?

	clientID?
	showRecentClientPaymentDialog?
	handleAcceptRecentClientPayment?
	handleCloseRecentClientPaymentDialog?

	// Terminal payment success modal
	showTerminalReceiptModal?
	terminalPaymentIntentId?
	handleCloseTerminalReceiptModal?

	// Save Card
	saveCard?
	setSaveCard?

	// Donation
	donationEnabled?: boolean
	promptDonation?: boolean
	setPromptDonation?: (value: boolean) => void

	// Tipping
	tippingEnabled?: boolean
	promptTipping?: boolean
	setPromptTipping?: (value: boolean) => void
	selectedTipRecipient?: SelectedTipRecipient
	setSelectedTipRecipient?: (value: SelectedTipRecipient) => void
}

const ReaderContext = createContext<IReaderContext>({})

export function useReader() {
	return useContext(ReaderContext)
}

declare let StripeTerminal: any // TODO TSC switch to @stripe/terminal-js/pure?

export function ReaderProvider({ children }: { children: ReactNode }) {
	const { currentPracticeObject, terminalLocation, currentUserConsolidator, currentConsolidator } =
		useCurrentPractice()
	const currentUser: User = useSelector(selectUser)
	const stripeAccount = currentPracticeObject?.stripeAccountId
	const { validateClientId } = useClientIdValidator()
	const { isInvoiceNumberValid } = useInvoiceNumberValidator()

	const { waitForPaymentIntentStatus } = usePaymentIntentStatusWatcher(
		currentPracticeObject,
		currentUserConsolidator
	)

	// To Do: replace reader as the parent state with connection status
	const [terminal, setTerminal] = useState(
		StripeTerminal.create({
			onFetchConnectionToken: fetchConnectionToken,
			onUnexpectedReaderDisconnect: unexpectedDisconnect,
			onConnectionStatusChange: connectionStatusChange
		})
	)
	const createdSimReader = useRef<any>()
	const serverDrivenTerminal = useMemo(
		() =>
			isServerDrivenTerminalEnabled(currentPracticeObject) ||
			isSurchargeEnabled(currentPracticeObject) ||
			blockAmexConsolidator(currentConsolidator) ||
			blockAmexPractice(currentPracticeObject) ||
			isLendingAtCheckoutEnabled(currentPracticeObject) ||
			isTerminalTippingEnabled(currentPracticeObject, currentConsolidator),
		[currentPracticeObject, currentConsolidator]
	)
	const [reader, setReader] = useState<any>()
	const readerConnectionModeRef = useRef<'sdk' | 'server-driven' | null>(null)
	const [readerSelectionInProgress, setReaderSelectionInProgress] = useState(false)
	const [discoveryInProgress, setDiscoveryInProgress] = useState(false)
	const [discoveredReaders, setDiscoveredReaders] = useState([])
	const [processingTerminalPayment, setProcessingTerminalPayment] = useState(false)
	const [chargeAmount, setChargeAmount] = useState('')
	const [chargeAmountError, setChargeAmountError] = useState('')
	const [chargeDescription, setChargeDescription] = useState('')
	const [clientID, setClientID] = useState('')
	const [clientIdError, setClientIdError] = useState('')
	const [invoiceID, setInvoiceID] = useState('')
	const [invoiceIdError, setInvoiceIdError] = useState('')
	const [connectionStatus, setConnectionStatus] = useState(terminal.getConnectionStatus())

	const [useSimulatedReader, setUseSimulatedReader] = useState(false)
	const [unsavedChanges, setUnsavedChanges] = useState(false)
	const [isInteracTransaction, setIsInteracTransaction] = useState(false)
	const [processingPaymentIntent, setProcessingPaymentIntent] =
		useState<PaymentIntentStatusAwaiter | null>(null)
	const [showTerminalReceiptModal, setShowTerminalReceiptModal] = useState(false)
	const [terminalPaymentIntentId, setTerminalPaymentIntentId] = useState<string>('')

	const isMvpClinic = useMemo(() => isMVPPractice(currentPracticeObject), [currentPracticeObject])
	const donationEnabled = useMemo(
		() =>
			isDonationEnabled(currentPracticeObject) ??
			consolidatorIsDonationEnabled(currentUserConsolidator),
		[currentPracticeObject, currentUserConsolidator]
	)
	const requireInvoiceNumber = useMemo(
		() => shouldRequireInvoiceNumber(currentUserConsolidator),
		[currentUserConsolidator]
	)
	const [saveCard, setSaveCard] = useState(false)
	const [promptDonation, setPromptDonation] = useState(true)

	const tippingEnabled = useMemo(
		() => isTerminalTippingEnabled(currentPracticeObject, currentConsolidator),
		[currentPracticeObject, currentUserConsolidator]
	)
	const tippingPromptByDefault = useMemo(
		() => getTerminalTippingPromptByDefault(currentPracticeObject, currentConsolidator),
		[currentPracticeObject, currentConsolidator]
	)
	const [promptTipping, setPromptTipping] = useState(false)
	const [selectedTipRecipient, setSelectedTipRecipient] = useState<SelectedTipRecipient>(null)

	useEffect(() => {
		setPromptTipping(tippingPromptByDefault)
		if (tippingPromptByDefault) {
			setPromptDonation(false)
		}
	}, [tippingPromptByDefault])

	const {
		checkIfShouldCreatePayment,
		showRecentClientPaymentDialog,
		handleAcceptRecentClientPayment,
		handleCloseRecentClientPaymentDialog
	} = useRecentClientPaymentConfirmation()
	async function connectToServerDrivenReader(selectedReader) {
		setReaderSelectionInProgress(true)
		setReader(selectedReader)
		setConnectionStatus('connected')
		setReaderSelectionInProgress(false)
		readerConnectionModeRef.current = 'server-driven'
		return selectedReader
	}

	async function connectToReader(selectedReader) {
		setReaderSelectionInProgress(true)
		try {
			const connectResult = await terminal.connectReader(selectedReader)
			const { reader: Reader } = await connectResult
			console.log('Reader, should == readerObject', Reader)
			const readerObject = await connectResult.reader
			console.log('Reader Connection Result', connectResult)
			console.log('readerObject', readerObject)
			if (connectResult.error) {
				console.log('Failed to connect:', connectResult.error)
				setReaderSelectionInProgress(false)
				return
			}
			console.log('Reader Connection Result', connectResult)
			console.log('reader', connectResult.reader)
			setReader(connectResult.reader)
			readerConnectionModeRef.current = 'sdk'
			setReaderSelectionInProgress(false)
			return Reader
		} catch (e) {
			console.log(e)
		}
	}

	async function disconnectReader() {
		try {
			const sdkConnection = readerConnectionModeRef.current === 'sdk'
			if (sdkConnection) {
				await terminal.disconnectReader()
			}
			setReader(undefined)
			setConnectionStatus(sdkConnection ? terminal.getConnectionStatus() : 'not_connected')
			readerConnectionModeRef.current = null
		} catch (e) {
			console.error('DisconnectReader Error', e)
		}
	}

	async function discoverReaderHandler() {
		try {
			setDiscoveryInProgress(true)
			if (!terminalLocation) {
				setDiscoveredReaders([])
				setDiscoveryInProgress(false)
				console.log('No Terminal Location Set')
				return
			}

			const config = { location: terminalLocation, simulated: false }
			const simulatedReaders: Record<string, unknown>[] = []
			if (!isProductionEnv() && useSimulatedReader) {
				config.simulated = true

				if (serverDrivenTerminal) {
					if (createdSimReader.current) {
						simulatedReaders.push(createdSimReader.current)
					} else {
						// If server driven, you have to create the terminal
						try {
							const simReader: Record<string, unknown> = await createSimulatedReader(
								currentPracticeObject?.id,
								config.location
							)
							createdSimReader.current = simReader
							simulatedReaders.push(simReader)
						} catch (err: any) {
							console.error('createSimulatedReader Error', err)
						}
					}
				}
			}

			if (simulatedReaders.length > 0) {
				setDiscoveredReaders(simulatedReaders as any)
			} else {
				const discoverResult = await terminal.discoverReaders(config)
				if (discoverResult.error) {
					setDiscoveryInProgress(false)
					toast.error('Something went wrong when searching, please try again.')
					console.error(
						'Something went wrong when searching, please try again',
						discoverResult.error
					)
					return
				}
				if (discoverResult.discoveredReaders.length === 0) {
					setDiscoveredReaders([])
					setDiscoveryInProgress(false)
					console.log('No card readers were discovered at your location')
					return
				}
				setDiscoveredReaders(discoverResult.discoveredReaders)
			}
			setDiscoveryInProgress(false)
		} catch (e) {
			logError(e, true, 'discoverReaderHandler Error')
			setDiscoveryInProgress(false)
		}
		setDiscoveryInProgress(false)
	}

	const resetFields = useCallback(() => {
		setProcessingTerminalPayment(false)
		setIsInteracTransaction(false)
		setChargeAmount('')
		setChargeDescription('')
		setClientID('')
		setSaveCard(false)
		setPromptDonation(tippingPromptByDefault ? false : true)
		setPromptTipping(tippingPromptByDefault)
		setSelectedTipRecipient(null)
	}, [setPromptDonation, tippingPromptByDefault])

	const shouldHideReceiptModal = useMemo(
		() =>
			practiceShouldHideReceiptModal(currentPracticeObject) ??
			consolidatorShouldHideReceiptModal(currentUserConsolidator) ??
			false,
		[currentPracticeObject, currentUserConsolidator]
	)

	const handleShowTerminalReceiptModal = useCallback(
		(paymentIntentId: string) => {
			if (shouldHideReceiptModal) return
			setTerminalPaymentIntentId(paymentIntentId)
			setShowTerminalReceiptModal(true)
		},
		[shouldHideReceiptModal]
	)

	const handleCloseTerminalReceiptModal = useCallback(() => {
		setShowTerminalReceiptModal(false)
		setTerminalPaymentIntentId('')
		resetFields()
	}, [resetFields])

	async function fetchConnectionToken() {
		try {
			const { data } = await getTerminalConnectionToken()
			return data.terminal_secret_token
		} catch (err) {
			console.error('fetchConnectionToken Error', err)
			return err
		}
	}

	async function unexpectedDisconnect() {
		try {
			console.log('Unexpected Disconnected from reader')
			setConnectionStatus('not_connected')
			// API call to get reader object based on this ReaderID
			// const stripe = require('stripe')('sk_test_h5drEhs1A0hBZAawI7jyhc1C00CSRBushi');
			// const reader = await stripe.terminal.readers.retrieve('tmr_P400-123-456-789');
			// https://stripe.com/docs/api/terminal/readers/retrieve
			await connectToReader(reader.id)
			// connectToReader(reader);
			// setReader();
		} catch (err) {
			console.log('TryError', err)
		}
	}

	async function connectionStatusChange() {
		setConnectionStatus(terminal.getConnectionStatus())
		console.log('Reader ConnectionStatus-Change', terminal.getConnectionStatus())
		console.log({ connectionStatus })
	}

	async function connectReaderHandler(terminalId) {
		try {
			setDiscoveryInProgress(true)
			const readers = await terminal.discoverReaders({
				location: terminalLocation
			})
			if (readers.error) {
				console.log('Unable to find readers', readers.error)
				return
			}
			const preferredReader = readers.discoveredReaders.find((r) => r.id === terminalId)
			await connectToReader(preferredReader)
		} catch (e) {
			console.log('connectReaderHandler Error', e)
			setDiscoveryInProgress(false)
		}
		setDiscoveryInProgress(false)
	}

	async function collectCardPayment() {
		if (!chargeAmount) return setChargeAmountError('Amount is required')
		if (isMvpClinic && !clientID) return setClientIdError('Client Id is required')
		if (saveCard && !clientID) return setClientIdError('Client Id is required when saving card')
		try {
			await validateClientId(clientID)
		} catch (err: any) {
			setClientIdError(err.message)
			return
		}
		if (requireInvoiceNumber && !invoiceID) return setInvoiceIdError('Invoice Number is required')
		if (invoiceID && !isInvoiceNumberValid(invoiceID))
			return setInvoiceIdError('Only numbers can be entered')

		const isReaderLocationMismatch =
			reader?.location && terminalLocation && reader.location !== terminalLocation
		if (isReaderLocationMismatch) {
			toast.error('Reader is registered to a different clinic. Please reconnect a reader.')
			disconnectReader()
			return
		}

		let toastId = displayLoadingToast('Connecting to Reader...')

		try {
			const amountInCents = convertDollarsToCents(+chargeAmount.replace(/,/g, ''))
			const [shouldCreatePayment, showToastAgain] = await checkIfShouldCreatePayment(
				clientID,
				stripeAccount!,
				amountInCents,
				toastId
			)

			if (!shouldCreatePayment) {
				return
			}

			if (showToastAgain) {
				toastId = displayLoadingToast('Connecting to Reader...')
			}

			setProcessingTerminalPayment(true)

			const paymentMethodTypes = ['card_present']
			if (isInteracTransaction) {
				paymentMethodTypes.push('interac_present')
			}

			const createCheckoutPaymentIntentResponse = await createPaymentIntent('card_present', {
				amount_due: Number(chargeAmount),
				practice_account_id: stripeAccount!,
				last_processed_by: currentUser.email,
				multiple_payment: false,
				auto_capture: true,
				no_checks: false,
				source: 'dashboard',
				created_from_mobile: isMobileDevice(),
				...(saveCard ? { save_payment_method: true } : {}),
				...(invoiceID && { invoice_number: invoiceID }),
				...(clientID && { client_id: clientID }),
				...(chargeDescription && { description: chargeDescription }),
				...(selectedTipRecipient && { recipient_id: selectedTipRecipient.id })
			})
			const createPaymentIntentResponse = {
				id: createCheckoutPaymentIntentResponse.data.payment_intent_id,
				client_secret: createCheckoutPaymentIntentResponse.data.payment_intent_client_secret
			}

			let collectPaymentMethodResponse
			if (serverDrivenTerminal) {
				try {
					toast.loading('Processing Payment...', {
						id: toastId,
						duration: 10000
					})
					try {
						collectPaymentMethodResponse = await processReaderPayment({
							readerId: reader.id,
							paymentIntentId: createPaymentIntentResponse.id,
							saveCard,
							promptDonation: donationEnabled ? promptDonation : undefined,
							promptTipping: tippingEnabled ? promptTipping : undefined
						})
					} catch (err) {
						// A reader timeout can be a false negative: the reader may still charge the card
						const outcome = await resolveReaderSendError(createPaymentIntentResponse.id, reader.id)
						if (outcome === READER_SEND_ERROR_OUTCOME.NOT_CHARGED) throw err

						logErrorBasedOnStatus(err)
						if (outcome === READER_SEND_ERROR_OUTCOME.UNCONFIRMED) {
							resetFields()
							warningToast(PAYMENT_STATUS_UNCONFIRMED_MESSAGE, toastId)
							return
						}
					}

					// Wait for payment completion via polling
					const paymentStatusAwaiter = waitForPaymentIntentStatus(createPaymentIntentResponse.id)
					setProcessingPaymentIntent(paymentStatusAwaiter)
					const paymentIntentStatus = await paymentStatusAwaiter.wait()

					setProcessingPaymentIntent(null)
					resetFields()
					if (paymentIntentStatus === PAYMENT_CANCELED) {
						toast.dismiss(toastId)
						return
					}

					// No final status arrived in time. The payment may still complete on the reader,
					// so point the user at Transactions instead of reporting a failure.
					if (paymentIntentStatus === PAYMENT_STATUS_TIMEOUT) {
						warningToast(PAYMENT_STATUS_UNCONFIRMED_MESSAGE, toastId)
						return
					}

					if (paymentIntentStatus === STATE_FAILED) {
						toast.error('Payment Failed!', {
							id: toastId,
							duration: 10000
						})
					} else if (paymentIntentStatus === STATE_AMEX_BLOCKED) {
						toast.error(DENIED_AMEX_CARD_MESSAGE, {
							id: toastId,
							duration: 10000
						})
					} else {
						handleShowTerminalReceiptModal(createPaymentIntentResponse.id)

						toast.success('Payment successful!', {
							id: toastId,
							duration: 10000
						})
					}
				} catch (err: any) {
					if (err?.message || err?.response?.data?.error_message) {
						toast.error(`Payment Failed: ${err?.response?.data?.error_message || err?.message}`, {
							id: toastId
						})
						logErrorBasedOnStatus(err)
					}
					resetFields()
				}
				return
			}

			const config_override = saveCard
				? {
						config_override: {
							allow_redisplay: 'always'
						}
					}
				: undefined

			collectPaymentMethodResponse = await terminal.collectPaymentMethod(
				createPaymentIntentResponse.client_secret,
				config_override
			)

			toast.loading('Getting Card Info...', {
				id: toastId,
				duration: 10000
			})
			toast.loading('Sending Card Info...', {
				id: toastId,
				duration: 10000
			})
			if (collectPaymentMethodResponse.error) {
				if (collectPaymentMethodResponse.error.code === 'canceled') {
					toast.dismiss(toastId)
					resetFields()
					return
				}
				toast.error(
					`Collect payment method failed: ${collectPaymentMethodResponse.error.message}`,
					{
						id: toastId
					}
				)
				resetFields()
				return
			}

			let processPaymentResponse
			if (collectPaymentMethodResponse.paymentIntent) {
				processPaymentResponse = await terminal.processPayment(
					collectPaymentMethodResponse.paymentIntent
				)
			}

			if (!processPaymentResponse || processPaymentResponse.error) {
				if (processPaymentResponse.error) {
					logErrorBasedOnStatus(processPaymentResponse.error)
					toast.error(`Confirm result failed: ${processPaymentResponse.error.message}`, {
						id: toastId
					})
				} else {
					toast.error('Something went wrong while processing payment.', {
						id: toastId
					})
				}
			} else if (
				processPaymentResponse.paymentIntent.status !== STATE_SUCCEEDED &&
				!isInteracTransaction
			) {
				try {
					await capturePaymentIntent(processPaymentResponse.paymentIntent.id)
					const paymentMethodDetails =
						processPaymentResponse.paymentIntent.charges?.data[0]?.payment_method_details
							?.card_present
					const amountCaptured = numeral(
						Number(processPaymentResponse.paymentIntent.amount) / 100.0
					).format('0,0.00')
					toast.success(
						<div>
							<br />
							<b>PAYMENT SUCCESSFUL!</b>
							<br />
							<br />
							Card: <b>{paymentMethodDetails?.brand.toUpperCase()} </b>
							<br />
							Last 4: <b>{paymentMethodDetails?.last4}</b>
							<br />
							Amount: <b>${amountCaptured}</b>
						</div>,
						{
							duration: 15000,
							id: toastId
						}
					)
					handleShowTerminalReceiptModal(createPaymentIntentResponse.id)
				} catch (e: any) {
					logError(e)
					toast.error(`${e.message}`, {
						id: toastId
					})
				}
			} else {
				handleShowTerminalReceiptModal(createPaymentIntentResponse.id)

				toast.success('Payment Successful!', {
					id: toastId
				})
			}
		} catch (e) {
			logError(e)
			errorToast(e, { id: toastId })
			// errorToast<CheckoutPaymentServiceErrorData>(e, { id: toastId });
		}
		resetFields()
	}

	function handleDescriptionChange(description) {
		setChargeDescription(description)
	}

	function handleChargeAmountChange(amount) {
		setChargeAmount(amount.replace(/,/g, ''))
		setChargeAmountError('')
	}

	function handleClientID(clientId) {
		setClientID(clientId)
		setClientIdError('')
	}

	function handleInvoiceIDChange(invoiceId) {
		setInvoiceID(invoiceId)
		if (!isInvoiceNumberValid(invoiceId)) {
			return setInvoiceIdError('Only numbers can be entered')
		}
		setInvoiceIdError('')
	}

	async function cancelServerDrivenReaderPayment() {
		const toastId = toast.loading('Clearing reader display...')
		try {
			await cancelReaderPayment(reader.id)
			if (processingPaymentIntent) {
				processingPaymentIntent.cancel()
				setProcessingPaymentIntent(null)
			}
			setChargeAmount('')
			setChargeDescription('')
			setClientID('')
			setInvoiceID('')
			setProcessingTerminalPayment(false)
			toast.success('Payment Canceled', {
				id: toastId
			})
		} catch (err: any) {
			console.error(err)
			toast.error(
				`Failed to clear reader display: ${err?.response?.data?.error_message || err?.message}`,
				{
					id: toastId
				}
			)
		}
	}

	async function simulateSwipe(brand: string, funding: string, error?: string) {
		const toastId = toast.loading('Simulating a swipe on the reader...')
		try {
			await simulateReaderSwipe(reader.id, brand, funding, error)
			toast.success('Swipe accepted', {
				id: toastId
			})
		} catch (err: any) {
			console.error(err)
			toast.error(
				`Failed to simulate a reader swipe: ${err?.response?.data?.error_message || err?.message}`,
				{
					id: toastId
				}
			)
		}
	}

	// 3b. CANCEL CARD PAYMENT
	async function cancelPayment() {
		if (serverDrivenTerminal) return cancelServerDrivenReaderPayment()
		try {
			await terminal.cancelCollectPaymentMethod()
			setChargeAmount('')
			setChargeDescription('')
			setClientID('')
			setInvoiceID('')
		} catch (err: any) {
			logErrorBasedOnStatus(err?.error || err, 'CancelPayment error')
		}
		await terminal.clearReaderDisplay()
	}

	async function clearReaderDisplay() {
		try {
			await terminal.clearReaderDisplay()
			setChargeAmount('')
			setChargeDescription('')
			setClientID('')
			setInvoiceID('')
		} catch (e) {
			logError(e, true, 'clearReaderDisplay Error')
		}
	}

	const handleTerminal = async () => {
		setTerminal(
			StripeTerminal.create({
				onFetchConnectionToken: fetchConnectionToken,
				onUnexpectedReaderDisconnect: unexpectedDisconnect,
				onConnectionStatusChange: connectionStatusChange
			})
		)
	}

	const logErrorBasedOnStatus = (err: any, customMessage?: string) => {
		const isPaymentCancelled = checkIsPaymentCancelledError(err)
		const isCardDeclined = checkIsCardDeclinedError(err)
		const isNoActivePaymentMethod = checkNoActivePaymentMethodError(err)
		const shouldLogOnSentry = !isPaymentCancelled && !isCardDeclined && !isNoActivePaymentMethod
		logError(err, shouldLogOnSentry, customMessage)
	}

	// TO DO: Is this useEffect needed, or can it be removed?
	useEffect(() => {
		window.addEventListener('terminal listener', handleTerminal)
		return () => {
			window.removeEventListener('remove terminal listener', handleTerminal)
		}
	}, [])

	useEffect(() => {
		if (!useSimulatedReader && reader) {
			disconnectReader()
		}
	}, [useSimulatedReader])

	// Delete the simulated reader before unloading
	useBeforeUnload(
		useCallback(() => {
			if (createdSimReader.current) {
				deleteSimulatedReader(createdSimReader.current.id).catch((err) => console.error(err))
			}
		}, [createdSimReader])
	)

	// Disconnect reader when practice changes to prevent cross-account payments (DASH-8794)
	useEffect(() => {
		if (reader && !processingTerminalPayment) {
			disconnectReader()
		}
	}, [terminalLocation])

	// TO DO: if the reader disconnects it is shown in Connection Status Change: not_connected
	useEffect(() => {
		window.addEventListener('terminal listener', handleTerminal)
		discoverReaderHandler()

		return () => {
			window.removeEventListener('remove terminal listener', handleTerminal)
		}
	}, [terminalLocation, useSimulatedReader])

	const context = {
		serverDrivenTerminal,
		terminal,
		cancelPayment,
		chargeAmount,
		clearReaderDisplay,
		collectCardPayment,
		connectionStatus,
		connectToReader,
		connectReaderHandler,

		setIsInteracTransaction,
		isInteracTransaction,

		disconnectReader,
		discoveredReaders,
		setDiscoveredReaders,
		discoverReaderHandler,
		discoveryInProgress,

		handleChargeAmountChange,
		handleClientID,
		handleDescriptionChange,
		handleInvoiceIDChange,

		setUseSimulatedReader,
		useSimulatedReader,
		simulateSwipe,

		processingTerminalPayment,

		reader,
		readerSelectionInProgress,
		unsavedChanges,
		setUnsavedChanges,
		connectToServerDrivenReader,
		cancelServerDrivenReaderPayment,
		chargeAmountError,
		clientIdError,
		invoiceIdError,

		clientID,
		showRecentClientPaymentDialog,
		handleAcceptRecentClientPayment,
		handleCloseRecentClientPaymentDialog,

		// Terminal payment success modal
		showTerminalReceiptModal,
		terminalPaymentIntentId,
		handleCloseTerminalReceiptModal,
		saveCard,
		setSaveCard,
		donationEnabled,
		promptDonation,
		setPromptDonation,
		tippingEnabled,
		promptTipping,
		setPromptTipping,
		selectedTipRecipient,
		setSelectedTipRecipient
	}

	return <ReaderContext.Provider value={context}>{children}</ReaderContext.Provider>
}

export default ReaderProvider
